import { FetchApi } from '@backstage/core-plugin-api';

export interface MovedMessageInfo {
  id: string;
  type: string;
  workspace: string;
  deliveredTo: string;
}

/**
 * DeadMessage properties and headers are actually dynamic, but we mapped the known ones
 */
export type DeadMessage = {
  isChecked?: boolean;
  payload: string;
  properties: {
    type: string;
    timestamp: number;
    message_id: string;
    headers: {
      exception_cause: string;
      workspace: string;
      workspace_alias: string;
      type: string;
      source: string;
    };
  };
};

export class FetchMqStatsService {
  constructor(
    private readonly proxyUrl: string,
    private readonly fetchApi: FetchApi,
    private readonly env: string,
    private readonly vhost: string,
  ) {}

  messagesCount(queue: string): Promise<number> {
    return this.fetchApi
      .fetch(
        `${this.proxyUrl}/rabbitmq-${this.env}/api/queues/${this.vhost}/${queue}?enable_queue_totals=true&disable_stats=true`,
      )
      .then(response => {
        if (!response.ok) return 0;
        return response.json().then(data => (data.messages as number) || 0);
      })
      .catch(() => 0);
  }

  queueStats(queue: string): Promise<Record<string, any>> {
    return this.fetchApi
      .fetch(
        `${this.proxyUrl}/rabbitmq-${this.env}/api/queues/${this.vhost}/${queue}`,
      )
      .then(response => response.json())
      .then(data => {
        const publish = data.message_stats?.publish_details?.rate || 0;
        const consume = data.message_stats?.ack_details?.rate || 0;

        const consumersCapacity = data.consumers_capacity || 0;
        
        return {
          speed: {
            inputRate: publish,
            outputRate: consume,
          },
          info: {
            State: data.state,
            Consumers: data.consumers || 0,
            'Consumer capacity': `${consumersCapacity*100} %`,
            Ready:   `${data.messages_ready || 0} msgs`,
            Unacked: `${data.messages_unacknowledged || 0} msgs`,
            Total:   `${data.messages || 0} msgs`,
            Publish: `${publish} msg/s`,
            'Consumer ack': `${consume} msg/s`,
          },
        };
      });
  }

  exchangeStats(exchange: string): Promise<Record<string, any>> {
    return this.fetchApi
      .fetch(
        `${this.proxyUrl}/rabbitmq-${this.env}/api/exchanges/${this.vhost}/${exchange}`,
      )
      .then(response => response.json())
      .then(data => {
        const publish = data.message_stats?.publish_in_details?.rate || 0;
        const consume = data.message_stats?.publish_out_details?.rate || 0;
        return {
          speed: {
            inputRate: publish,
            outputRate: consume,
          },
          info: {
            'Publish (in)': `${publish} msg/s`,
            'Publish (out)': `${consume} msg/s`,
          },
        };
      });
  }

  readMessages(queue: string, numberOfLoadedMessages: number): Promise<DeadMessage[]> {
    return this.fetchApi
      .fetch(
        `${this.proxyUrl}/rabbitmq-${this.env}/api/queues/${this.vhost}/${queue}/get`,
        this.buildReadMessagesRequest(numberOfLoadedMessages),
      )
      .then(response => {
        if (!response.ok) return [];
        return response.json() as Promise<DeadMessage[]>;
      })
      .catch(() => []);
  }

  private buildReadMessagesRequest(
    numberOfLoadedMessages: number,
  ): RequestInit {
    return {
      method: 'POST',
      body: JSON.stringify({
        count: numberOfLoadedMessages,
        ackmode: 'ack_requeue_true',
        encoding: 'auto',
      }),
    };
  }
}
